import type { DomainGoalRecord, EvidenceKind, FitnessDomain } from '~/models/fitnessDomain'
import type { PhaseRecord } from '~/models/phase'
import type { ProjectRecord } from '~/models/project'
import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { unwrapDate } from '~/types/dates'
import type { DomainGoalId, ProjectId } from '~/types/ids'

/**
 * The schema v3 backfill, as a pure function (D-090, D-092, D-031).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY THIS IS SHARED
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  The same reason `backfillProject.ts` is shared, and the same merge-blocking
 *  paired test: the database migration and the backup payload migration must
 *  agree exactly, and writing the rule twice would make the equivalence test a
 *  test of whether two people had the same idea. The *decision* lives here once;
 *  each side does only its own plumbing.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHAT IT DECIDES
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  **One body-composition domain goal per existing project, and nothing else.**
 *  Every project that exists before version 3 was created by a wizard that only
 *  ever asked about body composition, so that is the one domain the user
 *  demonstrably chose. It is `improve`, `primary`, `active`.
 *
 *  **Cardio, strength and mobility get no record at all.** This is the load-
 *  bearing omission. An absent domain goal is an inactive domain; writing a row
 *  with `intention: 'inactive'` would assert a choice the user was never offered,
 *  which is the same error as marking a backfilled project `completed` (D-080).
 *  It would also make every "which domains did you pick?" screen show four
 *  answers where the user gave one.
 *
 *  **A completed or abandoned project still gets its domain goal.** The project
 *  is history and its history is that it was about body composition. Skipping
 *  ended projects would leave a past project displaying no domains at all, which
 *  reads as data loss rather than as a project that predates the model.
 *
 *  **The status follows the project, not the phase.** An ended project's domain
 *  goal is `ended`; a running one's is `active`. A finished *phase* inside a
 *  running project changes nothing, exactly as it changed nothing at v2.
 *
 *  **`endedOn` is copied, never invented.** A project that ended without
 *  recording a date leaves the field absent rather than borrowing one.
 *
 *  **No clock, and no random identifier.** Every value is derived from the
 *  records themselves, which is what lets the two migrations produce byte-equal
 *  results from equal input and makes the paired test a plain comparison.
 *
 *  **No weekly commitment and no numeric target.** Nothing planned these, and a
 *  reconstruction would attribute a plan to Project Sola that it never made. The
 *  baseline is `known` where the project has a phase to have been measured for,
 *  and `baselineRequired` otherwise — an honest answer either way.
 */

/** The version this backfill produces. A literal, not `DB_SCHEMA_VERSION`:
    a released migration must keep stamping 3 after the constant moves on. */
export const DOMAINS_SCHEMA_VERSION = 3

/**
 * What a backfilled body-composition goal declares it collects.
 *
 * Exactly what the app already gathered before version 3 — weigh-ins,
 * circumferences and photos. Recovery is deliberately absent: it is a
 * cross-cutting guardrail rather than a body-composition evidence kind, and
 * listing it here would attach it to a domain that can be switched off.
 */
const BACKFILLED_EVIDENCE: readonly EvidenceKind[] = ['weighIn', 'circumference', 'photo']

const BODY_COMPOSITION: FitnessDomain = 'bodyComposition'

export interface DomainBackfillInput {
  readonly projects: readonly ProjectRecord[]
  readonly phases: readonly PhaseRecord[]
}

/** Only the records to write. An empty list means nothing to do. */
export interface DomainBackfillPlan {
  readonly domainGoals: readonly DomainGoalRecord[]
}

/**
 * Plan the backfill, or `undefined` when there is nothing to carry forward.
 *
 * A fresh install reaches version 3 with no projects, and inventing a domain goal
 * for it would put a goal in the user's plan before they had chosen one.
 */
export function planDomainBackfill(input: DomainBackfillInput): DomainBackfillPlan | undefined {
  const projects = [...input.projects].sort((left, right) => left.id.localeCompare(right.id))
  if (projects.length === 0) return undefined

  const phasesByProject = groupPhasesByProject(input.phases)

  const domainGoals = projects.map((project) =>
    backfillGoalFor(project, phasesByProject.get(project.id) ?? []),
  )

  return { domainGoals }
}

function backfillGoalFor(project: ProjectRecord, phases: readonly PhaseRecord[]): DomainGoalRecord {
  const ended = project.status !== 'active'

  /* The goal has existed for as long as the project has. Reading a clock here
     would stamp it with the moment the user happened to install an update, and
     would make the two migration paths disagree. */
  const createdAt: IsoTimestamp = project.createdAt

  return {
    /* Derived, not random. Two runs over the same data — the database one and
       the payload one — must produce the same identifier, or a restored backup
       would reference a domain goal that does not exist. The domain is part of
       the id so a second backfilled domain, if one is ever added, cannot
       collide with this one. */
    id: `backfill-${project.id}-${BODY_COMPOSITION}` as DomainGoalId,
    projectId: project.id,
    domain: BODY_COMPOSITION,
    intention: 'improve',
    priority: 'primary',
    status: ended ? 'ended' : 'active',
    direction: 'improve',
    /* A project with a phase was measured to produce that phase's target range,
       so its starting condition is on record. One without is honest about it. */
    baseline: phases.length > 0 ? 'known' : 'baselineRequired',
    evidenceKinds: BACKFILLED_EVIDENCE,
    startedOn: startDateFor(project, phases),
    ...(ended && project.endedOn !== undefined ? { endedOn: project.endedOn } : {}),
    /* No recorded change: nothing about this goal was ever changed by the user.
       An entry saying "created by migration" would put a change in a history
       whose purpose is to show what the user did. */
    changes: [],
    createdAt,
    updatedAt: createdAt,
    schemaVersion: DOMAINS_SCHEMA_VERSION,
  }
}

/**
 * When body composition started being worked on.
 *
 * The project's own start date, unless a phase predates it — which is possible
 * on a project the v2 migration itself backfilled, because that project's start
 * date was derived from the earliest record and a later correction to a phase
 * start (D-070) could have moved one earlier since. Taking the earlier of the two
 * keeps the goal from claiming to begin after evidence it covers.
 */
function startDateFor(project: ProjectRecord, phases: readonly PhaseRecord[]): CalendarDate {
  let earliest = project.startDate
  for (const phase of phases) {
    // Lexical comparison is exact for zero-padded YYYY-MM-DD.
    if (unwrapDate(phase.startDate) < unwrapDate(earliest)) earliest = phase.startDate
  }
  return earliest
}

function groupPhasesByProject(
  phases: readonly PhaseRecord[],
): Map<ProjectId, readonly PhaseRecord[]> {
  const grouped = new Map<ProjectId, PhaseRecord[]>()
  for (const phase of phases) {
    const projectId = phase.projectId
    /* A phase with no projectId predates v2 and cannot be attributed. It is left
       out rather than guessed at; it affects only the baseline flag, and guessing
       would be a fabrication for no gain. */
    if (projectId === undefined) continue
    const existing = grouped.get(projectId)
    if (existing === undefined) grouped.set(projectId, [phase])
    else existing.push(phase)
  }
  return grouped
}
